<template>
  <div class="space-y-4">
    <div v-if="showBalance" class="flex items-center justify-between gap-3">
      <p class="text-sm text-white/70">
        Balance:
        <span class="font-semibold text-white">{{ formatCredits(balanceCredits) }}</span>
        credits
      </p>
      <p v-if="balance?.low_balance" class="text-xs text-amber-300">Low balance</p>
    </div>

    <div
      v-if="highlighted"
      class="rounded-xl border border-echoes-primary/40 bg-echoes-primary/10 px-4 py-3"
    >
      <p class="text-sm text-white">Buy Benchmark credits to continue.</p>
    </div>

    <div v-if="isLoading" class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div
        v-for="n in 4"
        :key="n"
        class="rounded-xl border border-echoes-border-gray px-4 py-4 animate-pulse"
      >
        <div class="h-5 w-12 bg-white/10 rounded mb-2"></div>
        <div class="h-3.5 w-16 bg-white/5 rounded"></div>
      </div>
    </div>

    <div
      v-else
      class="grid grid-cols-2 sm:grid-cols-4"
      :class="compact ? 'gap-2' : 'gap-3'"
    >
      <button
        v-for="pack in packages"
        :key="pack.id"
        type="button"
        class="group relative rounded-xl border text-left transition-all duration-150 px-4 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-echoes-primary"
        :class="
          selectedPackageId === pack.id
            ? 'border-echoes-primary bg-echoes-primary/10'
            : 'border-echoes-border-gray hover:border-white/30 hover:bg-white/5'
        "
        @click="selectPackage(pack.id)"
      >
        <span
          v-if="selectedPackageId === pack.id"
          class="absolute top-3 right-3 size-4 rounded-full bg-echoes-primary flex items-center justify-center"
        >
          <i class="mdi mdi-check text-white text-[10px]"></i>
        </span>
        <p class="text-lg font-bold text-white">${{ pack.usd }}</p>
        <p class="text-sm text-white/50 mt-0.5">{{ formatCredits(pack.credits) }} credits</p>
      </button>

      <button
        type="button"
        class="group relative rounded-xl border text-left transition-all duration-150 px-4 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-echoes-primary"
        :class="
          selectedPackageId === 'custom'
            ? 'border-echoes-primary bg-echoes-primary/10'
            : 'border-echoes-border-gray hover:border-white/30 hover:bg-white/5'
        "
        @click="selectPackage('custom')"
      >
        <span
          v-if="selectedPackageId === 'custom'"
          class="absolute top-3 right-3 size-4 rounded-full bg-echoes-primary flex items-center justify-center"
        >
          <i class="mdi mdi-check text-white text-[10px]"></i>
        </span>
        <p class="text-lg font-bold text-white">Custom</p>
        <p class="text-sm text-white/50 mt-0.5">${{ custom.min_usd }}–${{ custom.max_usd }}</p>
      </button>
    </div>

    <div v-if="selectedPackageId === 'custom'" class="space-y-1">
      <label for="custom-credit-amount" class="text-sm text-white/70">Custom amount (USD)</label>
      <input
        id="custom-credit-amount"
        v-model="customAmountInput"
        type="number"
        min="1"
        step="1"
        class="w-full rounded-lg border border-echoes-border-gray bg-echoes-background-gray/40 px-3 py-2 text-white"
        :placeholder="`Enter an amount between ${custom.min_usd} and ${custom.max_usd}`"
      />
      <p v-if="customAmountError" class="text-xs text-red-400">{{ customAmountError }}</p>
    </div>

    <div
      v-if="!canCheckout"
      class="rounded-xl border border-echoes-border-gray/40 border-dashed py-6 text-center"
    >
      <i class="mdi mdi-credit-card-outline text-white/20 text-3xl mb-2 block"></i>
      <p class="text-sm text-white/40">
        {{ selectedPackageId === 'custom' ? 'Enter a valid custom amount to continue' : 'Select an amount above to continue' }}
      </p>
    </div>

    <div v-else class="rounded-xl border border-echoes-border-gray bg-echoes-background-gray/30">
      <div class="px-4 pt-4 pb-1 flex items-center justify-between">
        <p class="text-sm text-white/60">
          Paying
          <span class="text-white font-medium">${{ checkoutAmount }}</span>
          for
          <span class="text-white font-medium">{{ formatCredits(checkoutAmount) }} credits</span>
        </p>
        <button
          type="button"
          class="text-xs text-white/40 hover:text-white/70 transition-colors"
          @click="clearSelection"
        >
          Change
        </button>
      </div>
      <ApiCreditsPayment
        :key="paymentKey"
        :package-id="selectedPackageId === 'custom' ? 'custom' : selectedPackageId"
        :amount-usd="checkoutAmount"
        :element-id="elementId"
        :return-url="returnUrl"
        @payment-success="handlePaymentSuccess"
        @payment-error="emit('payment-error', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToast } from '@/composables/use-toast';
import ApiCreditsPayment from '@/components/modals/ApiCreditsPayment.vue';
import { getApiCreditPackages, getApiCreditsBalance } from '@/services/apiCreditsService.js';

const props = defineProps({
  compact: { type: Boolean, default: false },
  highlighted: { type: Boolean, default: false },
  showBalance: { type: Boolean, default: false },
  returnUrl: { type: String, default: '' },
  elementId: { type: String, default: 'api-credits-card-element' },
});

const emit = defineEmits(['payment-success', 'payment-error']);

const { addToast } = useToast();

const isLoading = ref(false);
const packages = ref([]);
const custom = ref({ min_usd: 5, max_usd: 500 });
const balance = ref(null);
const selectedPackageId = ref(null);
const customAmountInput = ref('');
const paymentFormKey = ref(0);

const selectedPackage = computed(() =>
  packages.value.find((pack) => pack.id === selectedPackageId.value),
);

const balanceCredits = computed(() => {
  if (balance.value?.balance_credits != null) {
    return balance.value.balance_credits;
  }
  const micro = Number(balance.value?.balance_micro);
  return Number.isFinite(micro) ? micro / 1000000 : 0;
});

const customAmount = computed(() => {
  const parsed = Number(customAmountInput.value);
  return Number.isInteger(parsed) ? parsed : NaN;
});

const customAmountError = computed(() => {
  if (selectedPackageId.value !== 'custom' || customAmountInput.value === '') return '';
  if (!Number.isInteger(customAmount.value)) {
    return 'Enter a whole dollar amount';
  }
  if (customAmount.value < custom.value.min_usd || customAmount.value > custom.value.max_usd) {
    return `Amount must be between $${custom.value.min_usd} and $${custom.value.max_usd}`;
  }
  return '';
});

const checkoutAmount = computed(() => {
  if (selectedPackageId.value === 'custom') {
    return customAmountError.value ? null : customAmount.value;
  }
  return selectedPackage.value ? Number(selectedPackage.value.usd) : null;
});

const canCheckout = computed(
  () => Number.isFinite(checkoutAmount.value) && checkoutAmount.value > 0,
);

const paymentKey = computed(
  () => `${selectedPackageId.value}-${checkoutAmount.value}-${paymentFormKey.value}`,
);

const formatCredits = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
};

const selectPackage = (id) => {
  selectedPackageId.value = selectedPackageId.value === id ? null : id;
};

const clearSelection = () => {
  selectedPackageId.value = null;
  customAmountInput.value = '';
};

const load = async () => {
  isLoading.value = true;
  try {
    const [packagesData, balanceData] = await Promise.all([
      getApiCreditPackages(),
      getApiCreditsBalance().catch(() => null),
    ]);
    packages.value = packagesData.packages;
    custom.value = packagesData.custom;
    balance.value = balanceData;
  } catch (error) {
    addToast(error.response?.data?.message || 'Unable to load Benchmark credit packages', 'error');
  } finally {
    isLoading.value = false;
  }
};

const waitForBalanceUpdate = async (previousBalanceMicro = 0) => {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    try {
      const nextBalance = await getApiCreditsBalance();
      balance.value = nextBalance;
      if (Number(nextBalance?.balance_micro ?? 0) > previousBalanceMicro) {
        return nextBalance;
      }
    } catch {
      // keep polling briefly while webhook settles
    }
  }
  return balance.value;
};

const handlePaymentSuccess = async () => {
  const previousBalanceMicro = Number(balance.value?.balance_micro ?? 0);
  addToast('Payment submitted. Updating Benchmark credit balance...', 'success');
  selectedPackageId.value = null;
  customAmountInput.value = '';
  const nextBalance = await waitForBalanceUpdate(previousBalanceMicro);
  paymentFormKey.value += 1;
  emit('payment-success', { balance: nextBalance });
};

onMounted(load);

defineExpose({
  reload: load,
  balance,
});
</script>
